import { Injectable, inject } from '@angular/core';
import { ActiveToast, IndividualConfig, ToastrService } from 'ngx-toastr';
import { ToastVariant } from '../components/controls/fnf-toast/fnf-toast.component';

/** Heading used when a caller does not supply one. */
const DEFAULT_TITLES: Record<ToastVariant, string> = {
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
  info: 'Information',
};

/**
 * Thin wrapper over ToastrService that names the four notification kinds and
 * fills in a default title, so screens do not each invent their own wording.
 *
 * ToastrService stays injectable for the call sites that already use it — both
 * render through FnfToastComponent.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly toastr = inject(ToastrService);

  /** Something the user asked for completed. */
  success(
    message: string,
    title?: string,
    config?: Partial<IndividualConfig>,
  ): ActiveToast<unknown> {
    return this.show('success', message, title, config);
  }

  /** Something failed and the user has to act. */
  error(message: string, title?: string, config?: Partial<IndividualConfig>): ActiveToast<unknown> {
    return this.show('error', message, title, config);
  }

  /** The action went through but something needs attention. */
  warning(
    message: string,
    title?: string,
    config?: Partial<IndividualConfig>,
  ): ActiveToast<unknown> {
    return this.show('warning', message, title, config);
  }

  /** Neutral, non-blocking news. */
  info(message: string, title?: string, config?: Partial<IndividualConfig>): ActiveToast<unknown> {
    return this.show('info', message, title, config);
  }

  /** Dismisses one toast, or the whole stack when no id is given. */
  clear(toastId?: number): void {
    this.toastr.clear(toastId);
  }

  private show(
    variant: ToastVariant,
    message: string,
    title?: string,
    config?: Partial<IndividualConfig>,
  ): ActiveToast<unknown> {
    return this.toastr[variant](message, title ?? DEFAULT_TITLES[variant], config);
  }
}
